import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from 'redux/reducers/settings'; // Assuming same baseQuery location
import { TMSResponse } from '.';
import { api } from './service-api';
// import { api, TMSResponse } from './service-api'; // Assuming you still need 'api' object for URL constants

// --- Interfaces ---

export interface PaginatedResponse<T = any> {
  count: number;
  next: number | null;
  previous: number | null;
  results: T;
}

export interface IProvince {
  created_at: string;
  id: number;
  name_en: string;
  name_np: string;
  province_code: string;
  province_number: number;
  updated_at: string;
}

export interface IDistricts {
  id: number;
  created_at: string;
  updated_at: string;
  name_en: string;
  name_np: string;
  province: number;
}

export interface IEthnics {
  id: number;
  name: string;
}

export interface IServiceGroup {
  id: number;
  name: string;
}

// --- API Definition ---

export const commonApi = createApi({
  reducerPath: 'commonApi',
  baseQuery: baseQuery,
  tagTypes: ['Ethnicity', 'ServiceGroup', 'Province', 'District', 'ServiceSubGroup'],
  endpoints: (build) => ({
    // 1. Fetch Ethnicities
    getEthnicities: build.query<IEthnics[], void>({
      query: () => api.location.ethnicity,
      transformResponse: (response: any) => response.data.ethnicities ?? []
    }),

    // 2. Fetch Service Groups
    getServiceGroups: build.query<IServiceGroup[], void>({
      query: () => api.location.serviceGroup,
      providesTags: ['ServiceGroup'],
      transformResponse: (response: TMSResponse<IServiceGroup[]>) => response.data
    }),

    // 3. Fetch Provinces
    getProvinces: build.query<IProvince[], void>({
      query: () => api.location.fetchProvinces,
      providesTags: ['Province'],
      transformResponse: (response: any) => response.data.provinces ?? []
    }),

    // 4. Fetch Districts (with Pagination & Province Filter)
    getDistricts: build.query<IDistricts[], string | undefined>({
      query: (provinceId?: string) => ({
        url: api.location.fetchDistricts,
        params: {
          province: provinceId,
          page: 1,
          page_size: 1000
        }
      }),
      providesTags: (result, error, provinceId) => [{ type: 'District', id: provinceId || 'ALL' }],
      transformResponse: (response: any) => response.data.districts ?? []
    }),

    getServiceSubGroup: build.query<any[], string>({
      query: (serviceId) => api.location.serviceSubGroup.replace('{serviceId}', serviceId),

      transformResponse: (response: any) => response.data ?? [],

      providesTags: (result, error, serviceId) => [{ type: 'ServiceSubGroup', id: serviceId }]
    }),
    getFileFromUrl: build.query<File, string>({
      query: (url) => ({
        url: url,
        responseHandler: (response: any) => response.blob() // Handle response as Blob
      }),
      keepUnusedDataFor: 0,
      transformResponse: (blob: Blob, meta, arg) => {
        const filename = arg.split('/').pop() || 'downloaded_file';
        return new File([blob], filename, { type: 'image/jpeg' });
      }
    })
  })
});

export const {
  useGetEthnicitiesQuery,
  useGetServiceGroupsQuery,
  useGetProvincesQuery,
  useGetDistrictsQuery,
  useGetServiceSubGroupQuery,
  useGetFileFromUrlQuery
} = commonApi;
